import getRoutePath from '../Routes/getRoutePath';
import { NOT_FOUND_PATH } from '../Routes/routeNames';

export type ContextValue = {
  isBrazeInitialized: boolean;
  inAppMessages: InAppMessage[];
  inAppMessageClick: (message: InAppMessage) => void;
  removeMessage: (message: InAppMessage) => void;
};

// https://js.appboycdn.com/web-sdk/latest/doc/modules/braze.html
export type Braze = {
  initialize: (
    apiKey: string,
    options?: {
      baseUrl: string;
      disablePushTokenMaintenance?: boolean;
      manageServiceWorkerExternally?: boolean;
      enableLogging?: boolean;
      localization: string;
      enableSdkAuthentication?: boolean;
      allowUserSuppliedJavascript?: boolean;
    }
  ) => boolean;
  openSession: () => void;
  wipeData: () => void;
  destroy: () => void;
  changeUser: (userId: string, signature?: string) => void;
  getUser: () => BrazeUser | undefined;
  subscribeToSdkAuthenticationFailures: (
    subscriber: (error: {
      errorCode: number;
      reason?: undefined | string;
      signature?: undefined | string;
      userId?: undefined | string;
    }) => void
  ) => string | undefined;
  setSdkAuthenticationSignature: (signature: string) => boolean;
  subscribeToInAppMessage: (
    subscriber: (inAppMessage: InAppMessage) => void
  ) => void;
  showInAppMessage: (messageId: InAppMessage) => void;
  logInAppMessageClick: (msg: InAppMessage) => void;
};

// the list of message types predetermined and designed in Figma for use in Braze inAppMessages
// https://www.figma.com/design/EUPz3rtLKx4YNCWFWRZfAq/Areena-Smart-DESIGN-720p?node-id=2366-107315&m=dev&focus-id=5744-5754
const inAppMessageTypes = ['message1', 'message2', 'message3'] as const;
export type InAppMessageTypes = (typeof inAppMessageTypes)[number];
export const isInAppMessageType = (x: any): x is InAppMessageTypes =>
  inAppMessageTypes.includes(x);

const inAppMessageTextStyle = ['dark', 'light'] as const;
export type InAppMessageTextStyle = (typeof inAppMessageTextStyle)[number];
export const isInAppMessageTextStyle = (x: any): x is InAppMessageTextStyle =>
  inAppMessageTextStyle.includes(x);

const inAppMessageActionButtonActions = ['redirect'] as const;
export type InAppMessageActionButtonActions =
  (typeof inAppMessageActionButtonActions)[number];
export const isInAppMessageActionButtonAction = (
  x: any
): x is InAppMessageActionButtonActions =>
  inAppMessageActionButtonActions.includes(x);

export const inAppMessageOptionalContentKeys = [
  'actionButtonLabel_fi',
  'actionButtonLabel_sv',
  'actionButtonAction',
  'redirect_uri',
  'text_style',
] as const;
export type InAppMessageOptionalContentKeysType =
  (typeof inAppMessageOptionalContentKeys)[number];
export const isInAppMessageOptionalContentKey = (
  x: any
): x is InAppMessageOptionalContentKeysType =>
  inAppMessageOptionalContentKeys.includes(x);

export const inAppMessageContentKeys = [
  'description_fi',
  'description_sv',
  'title_fi',
  'title_sv',
  'imgUrl',
  'type',
  'closeButtonLabel_fi',
  'closeButtonLabel_sv',
] as const;
export type InAppMessageContentKeysType =
  (typeof inAppMessageContentKeys)[number];
export const isInAppMessageContentKey = (
  x: any
): x is InAppMessageContentKeysType => inAppMessageContentKeys.includes(x);

export const isAllowedURIforBraze = (x: any): boolean => {
  return getRoutePath({ pointer: { uri: x } }) !== `/${NOT_FOUND_PATH}`;
};

export type InAppMessage = {
  extras: {
    description_fi: string;
    description_sv: string;
    title_fi: string;
    title_sv: string;
    imgUrl: string;
    type: InAppMessageTypes;
    closeButtonLabel_fi: string;
    closeButtonLabel_sv: string;
    actionButtonLabel_fi?: string;
    actionButtonLabel_sv?: string;
    actionButtonAction?: string;
    redirect_uri?: string;
    text_style?: InAppMessageTextStyle;
  };
  // triggerId is unique to a single campaign and its trigger in Braze
  triggerId: string;
  // controls are A/B test control groups, this tells if the current received
  // in app message is a control group instead of the main message
  isControl: boolean;
};

export type BrazeUser = {
  setLanguage: (language: string | null) => boolean;
};
